if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.input = this.querySelector('input[type="search"]');
      this.resultsContainer = this.querySelector(
        '.js-results-container'
      );
      this.btnSubmit = this.querySelector('.js-btn-submit');
      this.btnClearText = this.querySelector('.js-btn-clear-text');
      this.errorMessageContainer = this.querySelector(
        '.js-message-error'
      );
      this.errorMessageText =
        this.errorMessageContainer.querySelector('[data-text]');
      this.defaultErrorMessage = this.errorMessageText.textContent.trim();
      this.timeout = null;
      this.isOpen = false;
      this.requestId = 0;
      this.abortController = null;
      this.drawer = this.closest('.js-drawer');
      this.resultsContainer.id = `${this.input.id}-suggestions`;
      this.resultsContainer.setAttribute('role', 'region');
      this.resultsContainer.setAttribute('aria-label', this.input.labels?.[0]?.textContent.trim() || this.input.placeholder);
      ['role', 'aria-owns', 'aria-haspopup', 'aria-autocomplete', 'aria-expanded'].forEach(attribute => this.input.removeAttribute(attribute));
      this.input.setAttribute('aria-controls', this.resultsContainer.id);
      this.errorMessageContainer.setAttribute('role', 'alert');
      this.status = document.createElement('span');
      this.status.className = 'visually-hidden';
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.append(this.status);

      var searchDrawerContent = document.querySelector('.drawer__content--search');
      var searchSuggestions = document.querySelector('.search-drawer__suggestions');

      this.input.addEventListener('input', e => {
        clearTimeout(this.timeout);
        this.cancelRequest();

        this.timeout = setTimeout(() => {
          const searchTerm = this.input.value.trim();

          if (searchTerm.length <= 0) {
            this.close();
            if(DeviceDetector.isMobile() || DeviceDetector.isTablet()) {
              searchDrawerContent.style.height = 'auto';
            }
            if(searchSuggestions) {
              searchSuggestions.style.display = 'block';
            }
            return;
          }

          this.isOpen = true;
          this.getSearchResults(searchTerm);
          if(DeviceDetector.isMobile() || DeviceDetector.isTablet()) {
            searchDrawerContent.style.height = '100%';
          }
          if(searchSuggestions) {
            searchSuggestions.style.display = 'none';
          }
        }, 300);
      });

      this.input.addEventListener('keydown', e => {
        if (e.key === 'ArrowDown' && this.isOpen) {
          const firstResult = this.resultsContainer.querySelector('a[href], button:not([disabled])');
          if (firstResult) {
            e.preventDefault();
            firstResult.focus();
          }
          return;
        }
        const isEscape = e.key === 'Escape';

        if (!isEscape) {
          return;
        }

        if (!this.input.value) {
          if (this.drawer) {
            this.drawer.parentElement.parentElement.toggleDrawer();
          }

          return;
        }

        this.close();
      });

      this.btnClearText.addEventListener('click', e => {
        this.close();
        searchDrawerContent.style.height = 'auto';
        if(searchSuggestions) {
          searchSuggestions.style.display = 'block';
        }
      });
      this.addEventListener('keydown', e => {
        if (e.key === 'Escape' && e.target !== this.input && this.isOpen) {
          e.preventDefault();
          this.close();
        }
      });
    }

    async getSearchResults(searchTerm) {
      this.cancelRequest();
      const requestId = this.requestId;
      const controller = new AbortController();
      this.abortController = controller;
      this.resultsContainer.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(
          `${Shopify.routes.root}search/suggest?q=${encodeURIComponent(searchTerm)}&resources[type]=product,page,article,collection,query&resources[limit]=8&section_id=predictive-search`,
          { signal: controller.signal }
        );

        if (!this.isCurrentRequest(requestId, searchTerm)) {
          return;
        }

        if (!response.ok) {
          const error = new Error(response.status);

          throw error;
        }

        const text = await response.text();
        if (!this.isCurrentRequest(requestId, searchTerm)) return;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector(
            '#shopify-section-predictive-search'
          ).innerHTML;

        this.resultsContainer.innerHTML = resultsMarkup;
        this.open();
        this.status.textContent = this.resultsContainer.querySelector('[data-search-announcement]')?.dataset.searchAnnouncement || '';

        // new added product cards - quick cart triggers - set event listeners
        document.querySelector('quick-cart-drawer')?.initTriggers();

      } catch (error) {
        if (error.name === 'AbortError' || !this.isCurrentRequest(requestId, searchTerm)) return;
        this.errorMessageText.textContent = this.defaultErrorMessage;
        this.errorMessageContainer.classList.remove('hidden');
      } finally {
        if (requestId === this.requestId) {
          this.resultsContainer.setAttribute('aria-busy', 'false');
          this.abortController = null;
        }
      }
    }

    isCurrentRequest(requestId, searchTerm) {
      return this.isOpen && requestId === this.requestId && this.input.value.trim() === searchTerm;
    }

    cancelRequest() {
      this.requestId += 1;
      this.abortController?.abort();
      this.abortController = null;
      this.resultsContainer.setAttribute('aria-busy', 'false');
    }

    disconnectedCallback() {
      clearTimeout(this.timeout);
      this.cancelRequest();
    }

    open() {
      this.classList.add('is-open');
      this.resultsContainer.classList.remove('hidden');
      this.errorMessageContainer.classList.add('hidden');
      this.btnSubmit.setAttribute('tabindex', '-1');
      this.btnClearText.removeAttribute('disabled');
    }

    close() {
      clearTimeout(this.timeout);
      this.cancelRequest();
      this.isOpen = false;
      this.classList.remove('is-open');
      this.resultsContainer.classList.add('hidden');
      this.errorMessageContainer.classList.add('hidden');
      this.input.value = '';
      this.input.focus();
      this.btnSubmit.removeAttribute('tabindex');
      this.btnClearText.setAttribute('disabled', true);
      this.status.textContent = '';
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
